/**
 * HistoricalService - replays SEDC measured BEMS snapshots.
 *
 * The playback timer advances in 30-second historical increments. When a
 * playback date range is supplied, the service rolls from one day to the next
 * instead of stopping at 23:59:59.
 */

export type PlaybackState = 'stopped' | 'playing' | 'paused';

export interface PlaybackStatus {
    state: PlaybackState;
    date: string;           // YYYYMMDD
    seconds: number;        // seconds from midnight, 0-86399
    speed: number;          // playback speed multiplier
    pointCount: number;     // currently loaded point count
    rangeStart?: string;
    rangeEnd?: string;
    rangeIndex?: number;
    rangeCount?: number;
}

type Listener = (status: PlaybackStatus) => void;

export const HVAC_OPERATION_START_SECONDS = 9 * 3600;
export const HVAC_OPERATION_END_SECONDS = 18 * 3600;
export const HISTORICAL_TEST_WINDOW_START_SECONDS = HVAC_OPERATION_START_SECONDS - 3600;
export const HISTORICAL_TEST_WINDOW_END_SECONDS = HVAC_OPERATION_END_SECONDS + 3600;
const PLAYBACK_STORAGE_KEY = 'aees:historical-playback:v1';
const PLAYBACK_STORAGE_VERSION = 1;
const PLAYBACK_STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_VISUAL_PLAYBACK_SPEED = 30;
const MAX_RESTORED_PLAYBACK_SPEED = 10;

interface PersistedPlayback {
    version: number;
    savedAt: number;
    state: Exclude<PlaybackState, 'stopped'>;
    date: string;
    seconds: number;
    speed: number;
    playbackDates: string[];
}

// WHM values are cumulative meter readings. Convert interval deltas to kW and
// apply the CT/PT meter multiplier from the BEMS point table.
const WHM_METER_MULTIPLIER: Record<string, number> = {
    'POWER.WHM.LM_1A': 15,
    'POWER.WHM.LM_1B': 15,
    'POWER.WHM.LM_2A': 15,
    'POWER.WHM.LM_2B': 15,
    'POWER.WHM.LM_3A': 15,
    'POWER.WHM.LM_3B': 15,
    'POWER.WHM.P_GE': 30,
    'POWER.WHM.P_AHU': 20,
    'POWER.WHM.P_CH': 40,
    'POWER.WHM.P_EL': 10,
    'POWER.WHM.P_ELE': 15,
    'POWER.WHM.MC_A': 15,
    'POWER.WHM.MC_F': 0,
    'POWER.WHM.P_UPS': 25,
};

const KWH_TO_KW_30_SEC = 3600 / 30;
const WHM_UNIT_CUTOVER_DATE = '20210908';
const WHM_UNIT_CUTOVER_SECONDS = 14 * 3600;

class _HistoricalService {
    private _values: Record<string, number> = {};
    private _prevRawValues: Record<string, number> = {};
    private _state: PlaybackState = 'stopped';
    private _date = '';
    private _seconds = 0;
    private _speed = 5;
    private _playbackDates: string[] = [];
    private _intervalId: ReturnType<typeof setTimeout> | null = null;
    private _timerVersion = 0;
    private _listeners: Listener[] = [];
    private _restorePromise: Promise<boolean> | null = null;

    isActive(): boolean { return this._state !== 'stopped'; }
    isPlaying(): boolean { return this._state === 'playing'; }
    getState(): PlaybackState { return this._state; }

    getStatus(): PlaybackStatus {
        const rangeIndex = this._date ? this._playbackDates.indexOf(this._date) : -1;
        return {
            state: this._state,
            date: this._date,
            seconds: this._seconds,
            speed: this._speed,
            pointCount: Object.keys(this._values).length,
            rangeStart: this._playbackDates[0],
            rangeEnd: this._playbackDates.at(-1),
            rangeIndex: rangeIndex >= 0 ? rangeIndex : undefined,
            rangeCount: this._playbackDates.length || undefined,
        };
    }

    get(pointId: string): number | null {
        if (this._state === 'stopped') return null;
        return this._values[pointId] ?? null;
    }

    getSnapshot(): Record<string, number> {
        return { ...this._values };
    }

    setPlaybackDates(dates: string[]) {
        this._playbackDates = Array.from(new Set(
            dates.map(date => date.replaceAll('-', '')).filter(date => date.length === 8),
        )).sort();
    }

    getPlaybackDates(): string[] {
        return [...this._playbackDates];
    }

    restore(): Promise<boolean> {
        if (this.isActive()) return Promise.resolve(true);
        if (this._restorePromise) return this._restorePromise;
        this._restorePromise = this._restorePersistedState();
        return this._restorePromise;
    }

    async start(date: string, startSeconds = HISTORICAL_TEST_WINDOW_START_SECONDS, speed = 5, playbackDates?: string[]) {
        if (playbackDates) this.setPlaybackDates(playbackDates);
        const normalizedDate = date.replaceAll('-', '');
        if (!this._playbackDates.length && normalizedDate.length === 8) {
            this._playbackDates = [normalizedDate];
        }
        this._date = normalizedDate;
        this._seconds = this._clampToPlaybackWindow(startSeconds);
        this._speed = this._normalizeSpeed(speed);
        this._state = 'playing';
        this._values = {};
        this._prevRawValues = {};
        await this._primePreviousRawValues();
        await this._fetchSnapshot();
        this._notify();
        this._startTimer();
    }

    pause() {
        if (this._state !== 'playing') return;
        this._state = 'paused';
        this._stopTimer();
        this._notify();
    }

    resume() {
        if (this._state !== 'paused') return;
        this._state = 'playing';
        this._startTimer();
        this._notify();
    }

    stop() {
        this._state = 'stopped';
        this._values = {};
        this._prevRawValues = {};
        this._date = '';
        this._seconds = 0;
        this._stopTimer();
        this._notify();
    }

    async seek(seconds: number) {
        this._seconds = this._clampToPlaybackWindow(seconds);
        this._prevRawValues = {};
        await this._primePreviousRawValues();
        await this._fetchSnapshot();
        this._notify();
    }

    setSpeed(speed: number) {
        this._speed = this._normalizeSpeed(speed);
        if (this._state === 'playing') {
            this._stopTimer();
            this._startTimer();
        }
        this._notify();
    }

    subscribe(fn: Listener): () => void {
        this._listeners.push(fn);
        return () => { this._listeners = this._listeners.filter(listener => listener !== fn); };
    }

    private _nextPlaybackDate(): string | null {
        if (!this._playbackDates.length) return null;
        const currentIndex = this._playbackDates.indexOf(this._date);
        if (currentIndex < 0) return null;
        return this._playbackDates[currentIndex + 1] ?? null;
    }

    private _clampToPlaybackWindow(seconds: number): number {
        return Math.max(
            HISTORICAL_TEST_WINDOW_START_SECONDS,
            Math.min(HISTORICAL_TEST_WINDOW_END_SECONDS, seconds),
        );
    }

    private _normalizeSpeed(speed: number): number {
        return Number.isFinite(speed) && speed > 0
            ? Math.min(speed, MAX_VISUAL_PLAYBACK_SPEED)
            : 10;
    }

    private _startTimer() {
        this._stopTimer();
        const timerVersion = ++this._timerVersion;
        const tick = async () => {
            if (this._state !== 'playing') return;

            this._seconds += 30;
            if (this._seconds > HISTORICAL_TEST_WINDOW_END_SECONDS) {
                const nextDate = this._nextPlaybackDate();
                if (!nextDate) {
                    this._seconds = HISTORICAL_TEST_WINDOW_END_SECONDS;
                    this.pause();
                    return;
                }
                this._date = nextDate;
                this._seconds = HISTORICAL_TEST_WINDOW_START_SECONDS;
                this._prevRawValues = {};
                await this._primePreviousRawValues();
            }

            await this._fetchSnapshot();
            this._notify();

            if (this._state === 'playing' && timerVersion === this._timerVersion) {
                const intervalMs = Math.max(100, 30_000 / this._speed);
                this._intervalId = setTimeout(tick, intervalMs);
            }
        };
        const intervalMs = Math.max(100, 30_000 / this._speed);
        this._intervalId = setTimeout(tick, intervalMs);
    }

    private _stopTimer() {
        this._timerVersion += 1;
        if (this._intervalId) {
            clearTimeout(this._intervalId);
            this._intervalId = null;
        }
    }

    private async _fetchSnapshot() {
        const h = String(Math.floor(this._seconds / 3600)).padStart(2, '0');
        const m = String(Math.floor((this._seconds % 3600) / 60)).padStart(2, '0');
        const s = String(this._seconds % 60).padStart(2, '0');
        const time = `${h}:${m}:${s}`;
        const unitToKwh = this._whmUnitToKwh();
        try {
            const res = await fetch(`/api/historical/snapshot?date=${this._date}&time=${time}`);
            if (res.ok) {
                const json = await res.json();
                const raw: Record<string, number> = json.data || {};

                for (const [pt, multiplier] of Object.entries(WHM_METER_MULTIPLIER)) {
                    if (pt in raw) {
                        const cur = raw[pt];
                        const prev = this._prevRawValues[pt];
                        if (prev !== undefined && cur >= prev) {
                            raw[pt] = (cur - prev) * unitToKwh * multiplier * KWH_TO_KW_30_SEC;
                        } else {
                            raw[pt] = 0;
                        }
                        this._prevRawValues[pt] = cur;
                    }
                }

                this._values = raw;
            }
        } catch {
            // Keep the last good frame when the historical API is unavailable.
        }
    }

    private async _primePreviousRawValues() {
        if (!this._date || this._seconds < 30) return;
        const previousSeconds = this._seconds - 30;
        const h = String(Math.floor(previousSeconds / 3600)).padStart(2, '0');
        const m = String(Math.floor((previousSeconds % 3600) / 60)).padStart(2, '0');
        const s = String(previousSeconds % 60).padStart(2, '0');
        try {
            const res = await fetch(`/api/historical/snapshot?date=${this._date}&time=${h}:${m}:${s}`);
            if (!res.ok) return;
            const json = await res.json();
            const raw: Record<string, number> = json.data || {};
            for (const pointId of Object.keys(WHM_METER_MULTIPLIER)) {
                if (Number.isFinite(raw[pointId])) this._prevRawValues[pointId] = raw[pointId];
            }
        } catch {
            // The current frame can still load even when the preceding meter frame is unavailable.
        }
    }

    private async _restorePersistedState(): Promise<boolean> {
        const persisted = this._readPersistedState();
        if (!persisted) return false;

        this._playbackDates = persisted.playbackDates;
        if (!this._playbackDates.includes(persisted.date)) {
            this._playbackDates.push(persisted.date);
            this._playbackDates.sort();
        }
        this._date = persisted.date;
        this._seconds = this._clampToPlaybackWindow(persisted.seconds);
        this._speed = persisted.speed;
        this._state = persisted.state;
        this._values = {};
        this._prevRawValues = {};
        await this._primePreviousRawValues();
        await this._fetchSnapshot();
        this._notify();
        if (this._state === 'playing') this._startTimer();
        return true;
    }

    private _readPersistedState(): PersistedPlayback | null {
        if (typeof window === 'undefined') return null;
        try {
            const raw = window.localStorage.getItem(PLAYBACK_STORAGE_KEY);
            if (!raw) return null;
            const value = JSON.parse(raw) as Partial<PersistedPlayback>;
            const validState = value.state === 'playing' || value.state === 'paused';
            const validDate = typeof value.date === 'string' && /^\d{8}$/.test(value.date);
            const fresh = typeof value.savedAt === 'number'
                && Date.now() - value.savedAt <= PLAYBACK_STORAGE_TTL_MS;
            if (
                value.version !== PLAYBACK_STORAGE_VERSION
                || !validState
                || !validDate
                || !fresh
            ) {
                window.localStorage.removeItem(PLAYBACK_STORAGE_KEY);
                return null;
            }
            return {
                version: PLAYBACK_STORAGE_VERSION,
                savedAt: value.savedAt!,
                state: value.state as Exclude<PlaybackState, 'stopped'>,
                date: value.date!,
                seconds: Number.isFinite(value.seconds)
                    ? Number(value.seconds)
                    : HISTORICAL_TEST_WINDOW_START_SECONDS,
                speed: Math.min(this._normalizeSpeed(Number(value.speed)), MAX_RESTORED_PLAYBACK_SPEED),
                playbackDates: Array.isArray(value.playbackDates)
                    ? value.playbackDates
                        .map(date => String(date).replaceAll('-', ''))
                        .filter(date => /^\d{8}$/.test(date))
                    : [value.date!],
            };
        } catch {
            return null;
        }
    }

    private _persistState() {
        if (typeof window === 'undefined') return;
        try {
            if (this._state === 'stopped' || !this._date) {
                window.localStorage.removeItem(PLAYBACK_STORAGE_KEY);
                return;
            }
            const payload: PersistedPlayback = {
                version: PLAYBACK_STORAGE_VERSION,
                savedAt: Date.now(),
                state: this._state,
                date: this._date,
                seconds: this._seconds,
                speed: this._speed,
                playbackDates: this.getPlaybackDates(),
            };
            window.localStorage.setItem(PLAYBACK_STORAGE_KEY, JSON.stringify(payload));
        } catch {
            // Persistence is optional in restricted browser contexts.
        }
    }

    private _whmUnitToKwh(): number {
        if (this._date > WHM_UNIT_CUTOVER_DATE) return 1000;
        if (this._date === WHM_UNIT_CUTOVER_DATE && this._seconds >= WHM_UNIT_CUTOVER_SECONDS) return 1000;
        return 1;
    }

    private _notify() {
        this._persistState();
        const status = this.getStatus();
        this._listeners.forEach(fn => fn(status));
    }
}

export const HistoricalService = new _HistoricalService();
